/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the strict publish (docs/SPEC_WORKFLOW.md §7).

   A publish is ONE action that cannot half-land, and it is complete only
   when every affected variant has answered. This module is the pure brain
   of that: what the publish offers each variant (`planPublish`), whether
   the dialog's answers are complete (`isPublishReady`), and the one set of
   records the batch writes (`buildPublishResult` → `publishWrites`).

   The variant maths is the shared brain (shared/variant-updates.ts) — the
   SAME hold + decision functions the server applies when an offer is
   answered later — so a variant answered in the dialog ends up exactly
   where it would have after answering the offer in the Updates panel.
   ═══════════════════════════════════════════════════════════════ */

import type {
  ClientLayer,
  ContentBlock,
  SpecMaster,
  SpecMasterDraft,
  SpecMasterVersion,
} from '../../shared/types';
import {
  applyDecision,
  buildVariantChangeEntries,
  holdLayersForEntries,
  pendingCount,
  type IdContext,
  type VariantChangeEntry,
} from '../../shared/variant-updates';
import { buildInlineDiff } from '../../shared/block-diff';
import { hashBlock } from '../../shared/spec-compose';
import { pushEditHistory } from '../../shared/editHistory';
import type { BatchWrite } from '../services/cloudCache';

export type ChangeDecision = 'accepted' | 'rejected';
/** entryId → changeId → answer. Only PENDING changes need one. */
export type PublishAnswers = Record<string, Record<string, ChangeDecision>>;

export interface PublishPlan {
  /** The published master as the server has it now. */
  master: SpecMaster;
  /** The draft being published (mirror flushed). */
  draft: SpecMasterDraft;
  fromVersion: number;
  toVersion: number;
  /** The layers the publish carries — the draft's, un-held. */
  layers: ClientLayer[];
  /** One entry per variant the publish touches, built from the UN-held layers. */
  entries: VariantChangeEntry[];
  /** Blocks that differ between published and draft (the dialog's count). */
  changedBlocks: number;
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

export function planPublish(args: {
  master: SpecMaster;
  draft: SpecMasterDraft;
  by: string;
  ctx?: IdContext;
}): PublishPlan {
  const { master, draft, by, ctx } = args;
  const prevCore = master.coreContent || [];
  const nextCore = draft.coreContent || [];
  // A draft from before migration 044 carries no `layers` — publishing it
  // must never wipe the master's.
  const layers = (draft.layers ?? master.layers ?? []) as ClientLayer[];
  const fromVersion = master.version || 0;
  const toVersion = fromVersion + 1;
  const entries = buildVariantChangeEntries({
    specMasterId: master.id,
    fromVersion,
    toVersion,
    prevCore,
    nextCore,
    layers,
    createdBy: by,
    ctx,
  });
  const changedBlocks = buildInlineDiff(prevCore, nextCore).filter(
    (b) => b.kind !== 'unchanged'
  ).length;
  return { master, draft, fromVersion, toVersion, layers, entries, changedBlocks };
}

/** The variants the dialog must walk: every entry with a change still to
 *  answer (a change on a block the variant hides arrives already decided). */
export function affectedEntries(plan: Pick<PublishPlan, 'entries'>): VariantChangeEntry[] {
  return plan.entries.filter((e) => pendingCount(e) > 0);
}

/** Answer every pending change of one entry the same way. */
export function answerEntry(
  entry: VariantChangeEntry,
  decision: ChangeDecision
): Record<string, ChangeDecision> {
  const out: Record<string, ChangeDecision> = {};
  for (const c of entry.changes || []) if (c.decision === 'pending') out[c.changeId] = decision;
  return out;
}

/** How many of an entry's pending changes have an answer. */
export function answeredCount(entry: VariantChangeEntry, answers: PublishAnswers): number {
  const mine = answers[entry.id] || {};
  return (entry.changes || []).filter((c) => c.decision === 'pending' && !!mine[c.changeId]).length;
}

/** The Publish button's gate: every pending change of every affected
 *  variant has an answer. A publish that touches no variant is ready. */
export function isPublishReady(
  plan: Pick<PublishPlan, 'entries'>,
  answers: PublishAnswers
): boolean {
  return affectedEntries(plan).every((e) => answeredCount(e, answers) === pendingCount(e));
}

export interface PublishResult {
  master: SpecMaster;
  /** Every entry, fully decided — history rows, nothing left pending. */
  entries: VariantChangeEntry[];
}

/** The new master + decided entries. Throws if an answer is missing (the
 *  dialog never lets that happen; this is the last fence). */
export function buildPublishResult(
  plan: PublishPlan,
  answers: PublishAnswers,
  opts: { by: string; now?: string; ctx?: IdContext }
): PublishResult {
  if (!isPublishReady(plan, answers)) {
    throw new Error('Every affected variant must answer before this publish');
  }
  const now = opts.now || new Date().toISOString();
  const ctx: IdContext = { ...(opts.ctx || {}), by: opts.by, now };
  const { master, draft } = plan;

  // HOLD first (what an unanswered offer would look like), then apply each
  // answer — the exact sequence the Updates panel runs, in one step.
  let layers = holdLayersForEntries(plan.layers, plan.entries, ctx);
  const entries: VariantChangeEntry[] = [];
  for (const entry of plan.entries) {
    let current = entry;
    const mine = answers[entry.id] || {};
    for (const change of entry.changes || []) {
      if (change.decision !== 'pending') continue;
      const layer = layers.find((l) => l.id === entry.layerId);
      if (!layer) continue;
      const res = applyDecision(current, layer, change.changeId, mine[change.changeId], ctx);
      current = res.entry;
      layers = layers.map((l) => (l.id === layer.id ? res.layer : l));
    }
    entries.push(current);
  }

  const snapshot: SpecMasterVersion = {
    version: master.version,
    snapshot: {
      coreContent: clone(master.coreContent || []),
      systems: clone(master.systems || []),
      title: master.title,
      description: master.description,
    },
    savedBy: master.modifiedBy || 'unknown',
    savedAt: master.lastModified,
  };
  const maxVersions = master.maxVersionsKept || 10;
  const next: SpecMaster = {
    ...master,
    versionHistory: [...(master.versionHistory || []), snapshot].slice(-maxVersions),
    coreContent: clone(draft.coreContent || []) as ContentBlock[],
    systems: clone(draft.systems || []),
    variants: clone(draft.variants || []),
    layers,
    version: plan.toVersion,
    modifiedBy: opts.by,
    status: 'published',
    lastModified: now,
    editHistory: pushEditHistory(master.editHistory, opts.by, undefined, 'published'),
    // The lock is cleared — an explicit `undefined` key serializes as NULL.
    lockedBy: undefined,
    lockedAt: undefined,
  };
  return { master: next, entries };
}

/** The ONE publish transaction, in order: the master row, the decided feed
 *  rows (only when a variant was touched), then every draft row for this
 *  master (the shared one and any pre-collab per-user drafts). */
export function publishWrites(
  result: PublishResult,
  draftIds: string[],
  build: {
    saveMaster: (m: SpecMaster) => BatchWrite;
    createEntries: (e: VariantChangeEntry[]) => BatchWrite;
    removeDraft: (id: string) => BatchWrite;
  }
): BatchWrite[] {
  const writes: BatchWrite[] = [build.saveMaster(result.master)];
  if (result.entries.length) writes.push(build.createEntries(result.entries));
  for (const id of [...new Set(draftIds)]) writes.push(build.removeDraft(id));
  return writes;
}

/** What one entry OFFERS, independent of its random ids: the variant, and
 *  per change the block, the kind and the exact words before/after. Two
 *  entries with the same key ask the variant the same questions. */
export function entryKey(entry: VariantChangeEntry): string {
  const changes = (entry.changes || [])
    .map(
      (c) =>
        `${c.blockId}:${c.kind}:${c.decision === 'pending' ? 'p' : c.decision}:` +
        `${c.beforeBlock ? hashBlock(c.beforeBlock) : '-'}>${c.afterBlock ? hashBlock(c.afterBlock) : '-'}`
    )
    .sort();
  return `${entry.layerId ?? ''}|${changes.join(',')}`;
}

/** The stale-dialog guard: were the answers given against the same offers
 *  the LIVE draft makes now? Order-free; compares by entry key. */
export function sameOffers(
  answered: Pick<PublishPlan, 'entries'>,
  live: Pick<PublishPlan, 'entries'>
): boolean {
  const a = answered.entries.map(entryKey).sort();
  const b = live.entries.map(entryKey).sort();
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

/** Carry the dialog's answers onto a re-planned publish whose offers are the
 *  SAME (sameOffers) — the new plan mints fresh entry/change ids. */
export function carryAnswers(
  answered: Pick<PublishPlan, 'entries'>,
  live: Pick<PublishPlan, 'entries'>,
  answers: PublishAnswers
): PublishAnswers {
  const byKey = new Map(answered.entries.map((e) => [entryKey(e), e]));
  const out: PublishAnswers = {};
  for (const entry of live.entries) {
    const old = byKey.get(entryKey(entry));
    if (!old) continue;
    const mine = answers[old.id] || {};
    const next: Record<string, ChangeDecision> = {};
    for (const change of entry.changes || []) {
      const was = (old.changes || []).find(
        (c) => c.blockId === change.blockId && c.kind === change.kind
      );
      if (was && mine[was.changeId]) next[change.changeId] = mine[was.changeId];
    }
    out[entry.id] = next;
  }
  return out;
}

/** "Publish failed: <message> (step 2)" — the step is 1-based. */
export function publishErrorMessage(err: unknown): string {
  const e = err as { message?: unknown; failedOp?: unknown } | null;
  const msg = typeof e?.message === 'string' && e.message ? e.message : 'Unknown error';
  const step = typeof e?.failedOp === 'number' ? ` (step ${e.failedOp + 1})` : '';
  return `Publish failed: ${msg}${step}`;
}
